import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaSearch, FaImage, FaVideo, FaCalendar, FaEdit
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/public/BottomNav';
import SideMenu from '../components/public/SideMenu';
import LanguageSelector from '../components/public/LanguageSelector';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { wishesService } from '../services/wishesService';
import logo from '../assets/logo.svg';
import './WishesGallery.css';
import './Home.css';

const WishesGallery = () => {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSideMenu, setShowSideMenu] = useState(false);
  const [media, setMedia] = useState([]);
  const [filteredMedia, setFilteredMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDate, setSelectedDate] = useState(null);

  const categories = [
    { value: 'all', key: 'all' },
    { value: 'Festival', key: 'festival' },
    { value: 'Birthday', key: 'birthday' },
    { value: 'Anniversary', key: 'anniversary' },
    { value: 'Holiday', key: 'holiday' },
    { value: 'Other', key: 'other' }
  ];

  useEffect(() => {
    // Get date from URL parameter if available
    const params = new URLSearchParams(location.search);
    const dateParam = params.get('date');
    if (dateParam) {
      console.log('Date filter applied from URL:', dateParam);
      setSelectedDate(dateParam);
    }
    fetchWishesMedia();
  }, [location.search]);

  useEffect(() => {
    filterMedia();
  }, [searchTerm, selectedCategory, media, selectedDate]);

  const fetchWishesMedia = async () => {
    try {
      setLoading(true);
      const response = await wishesService.getWishesGallery();

      // Helper function to build proper URLs
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const buildUrl = (url) => {
        if (!url) return null;
        // If URL already starts with http/https, return as-is (Cloudinary URLs)
        if (url.startsWith('http://') || url.startsWith('https://')) {
          return url;
        }
        // Otherwise prepend baseURL for local server URLs
        return `${baseURL}${url}`;
      };

      const mediaWithUrls = response.data.map(item => ({
        ...item,
        mediaUrl: buildUrl(item.mediaUrl),
        thumbnailUrl: buildUrl(item.thumbnailUrl)
      }));

      setMedia(mediaWithUrls);
      setFilteredMedia(mediaWithUrls);
    } catch (error) {
      console.error('Error fetching wishes media:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterMedia = () => {
    let filtered = media;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.occasionName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by date
    if (selectedDate) {
      console.log('Filtering by date:', selectedDate);
      console.log('Total items before date filter:', filtered.length);
      filtered = filtered.filter(item => {
        if (!item.dates || item.dates.length === 0) return false;
        // Check if any of the item's dates match the selected date
        return item.dates.some(itemDate => {
          const itemDateStr = new Date(itemDate).toISOString().split('T')[0];
          const matches = itemDateStr === selectedDate;
          if (matches) {
            console.log(`Found match: ${item.occasionName} on ${itemDateStr}`);
          }
          return matches;
        });
      });
      console.log('Total items after date filter:', filtered.length);
    }

    setFilteredMedia(filtered);
  };

  const handleMediaClick = (mediaId) => {
    navigate(`/wishes/editor/${mediaId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="home-page">
      {/* Header is now hidden - logo and menu moved to bottom nav */}
      
      <div className="wishes-gallery-container full-height">
        {/* Page Header */}
        <div className="wishes-gallery-header">
          <h2>{t('wishes_gallery')}</h2>
          <p>{t('wishes_gallery_subtitle')}</p>
        </div>

        {/* Search Bar */}
        <div className="wishes-filters">
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder={t('search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Date Filter Display */}
        {selectedDate && (
          <div className="date-filter-display">
            <p>
              Filtered by date: <strong>{new Date(selectedDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
            </p>
            <button
              className="clear-date-filter"
              onClick={() => {
                setSelectedDate(null);
                navigate('/wishes');
              }}
            >
              Clear Date Filter
            </button>
          </div>
        )}

        {/* Category Filters - Always Visible */}
        <div className="category-filters">
          {categories.map(category => (
            <button
              key={category.value}
              className={`category-btn ${selectedCategory === category.value ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.value)}
            >
              {t(category.key)}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="wishes-gallery-grid">
          {loading ? (
            <LoadingSpinner size={50} color="#244A95" text={t('loading')} />
          ) : filteredMedia.length === 0 ? (
            <div className="no-media">
              <p>{t('no_media_found')}</p>
            </div>
          ) : (
            filteredMedia.map(item => (
              <div
                key={item._id}
                className="media-card"
                onClick={() => handleMediaClick(item._id)}
              >
                <div className="media-thumbnail">
                  {item.mediaType === 'video' ? (
                    <>
                      <img
                        src={item.thumbnailUrl || '/placeholder-video.png'}
                        alt={item.occasionName}
                      />
                      <div className="edit-icon-overlay">
                        <FaEdit />
                      </div>
                      <div className="media-type-badge video">
                        <FaVideo />
                      </div>
                    </>
                  ) : (
                    <>
                      <img
                        src={item.mediaUrl}
                        alt={item.occasionName}
                      />
                      <div className="media-type-badge image">
                        <FaImage />
                      </div>
                    </>
                  )}
                  <div className="media-overlay">
                    <div className="media-overlay-content">
                      <h4 className="overlay-festival-name">{item.occasionName}</h4>
                      {item.dates && item.dates.length > 0 && (
                        <p className="overlay-date">
                          <FaCalendar /> {formatDate(item.dates[0])}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="media-info">
                  <h3>{item.occasionName}</h3>
                  <div className="media-details">
                    <span className="category-badge">{item.category}</span>
                    {item.dates && item.dates.length > 0 && (
                      <span className="date-badge">
                        <FaCalendar /> {formatDate(item.dates[0])}
                      </span>
                    )}
                  </div>
                  {item.tags && item.tags.length > 0 && (
                    <div className="media-tags">
                      {item.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <BottomNav 
        activeTab="wishes" 
        logo={logo}
        onMenuClick={() => setShowSideMenu(true)}
        onLanguageClick={() => navigate('/languages')}
        onThemeClick={toggleTheme}
        theme={theme}
        isAuthenticated={isAuthenticated}
        onLogout={logout}
        t={t}
      />
      <SideMenu isOpen={showSideMenu} onClose={() => setShowSideMenu(false)} />
      <LanguageSelector />
    </div>
  );
};

export default WishesGallery;
